import {
  Button,
  Card,
  CardBody,
  Container,
  Grid,
  GridItem,
  Heading,
  HStack,
  Modal,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Tag,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getFreightServices } from '@pelicargo/tenants';
import {
  AircraftType,
  CargoItem,
  FreightService,
  NotificationCategory,
  RequestFormValues,
  WeightUnit,
} from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  freightStatusOptions,
  getHandlingAndPacking,
  getPrettyAircraftType,
  getPrettyCargoType,
  getPrettyCommodity,
  getPrettyDimensionUnit,
  join,
  lbToKg,
  prettyDate,
  prettyNumber,
} from '@pelicargo/utils';
import { omit, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { HiPencil } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { z } from 'zod';

import { DefaultContactsCard } from '../../components/DefaultContactsCard';
import { SimpleRequestForm } from '../../components/Forms/SimpleRequestForm';
import { Hint } from '../../components/Hint';
import { LabeledValue } from '../../components/LabeledValue';
import { Page } from '../../components/Page';
import { SimpleCheckbox } from '../../components/SimpleCheckbox';
import { SimpleForm } from '../../components/SimpleForm';
import { SimpleInput } from '../../components/SimpleInput';
import { SimpleSwitch } from '../../components/SimpleSwitch';
import { SimpleTextarea } from '../../components/SimpleTextarea';
import { trpc } from '../../config/trpc';
import { useCreateRequest } from '../../hooks/request/useCreate';
import { defaultRequestFormValues } from '../../hooks/request/useForm';
import { useRequestPickupOrigin } from '../../hooks/request/useRequestPickupOrigin';
import { useTargetAccount } from '../../hooks/useTargetAccount';
import { CONTAINER_2XL } from '../../utils/constants';
import { formatRequestForm } from '../../utils/formatRequestForm';
import { Calculations } from '../../utils/types';

type FormValues = {
  additional_information: string;
  customer_ref: string;
  target_rate: number;
  freight_status: string;
  is_terms_accepted: boolean;
  default_contacts: {
    contact_id: string;
    updates_for: NotificationCategory[];
  }[];
};

const validationSchema = z.object({
  additional_information: z.string().nullish(),
  customer_ref: z.string().nullish(),
  target_rate: z.coerce
    .number()
    .transform((value) => (isNaN(value) || value === null || value === undefined ? undefined : value))
    .pipe(z.number().optional()),
  freight_status: z.enum(['Quoting', 'Confirmed']).nullish(),
  is_terms_accepted: z
    .boolean()
    .refine((val) => val, 'You must accept the Terms of Service and Privacy Policy in order to proceed.'),
  default_contacts: z.array(
    z.object({
      contact_id: z.coerce.number(),
      updates_for: z
        .array(z.nativeEnum(NotificationCategory, { required_error: 'Notification category is required' }))
        .optional(),
    }),
  ),
});

export const RequestQuote = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const { onOpen, onClose, isOpen } = useDisclosure();
  const { isOpen: showAllCargo, onToggle: toggleShowAllCargo } = useDisclosure({ defaultIsOpen: false });
  const { targetAccount } = useTargetAccount();
  const [createRequest, isLoading] = useCreateRequest();
  const { data: default_contacts } = trpc.getDefaultContactsByAccount.useQuery(
    { id: targetAccount?.id, updates_for: NotificationCategory.Quote },
    { enabled: !!targetAccount.id },
  );
  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    resolver: zodResolver(validationSchema),
  });
  const [request, setRequest] = useSessionStorage<RequestFormValues>('request', {} as any);
  const [airportIds, setAirportIds] = useState({ from: [], to: [] });

  const { data: providers } = trpc.getProvidersForAirport.useQuery(
    { from: airportIds?.from, to: airportIds?.to },
    { enabled: !!airportIds?.from?.length && !!airportIds?.to?.length },
  );

  useEffect(() => {
    if (request) {
      const originAirportIds = request?.origin_airports;
      const destinationAirportIds = request?.destination_airports;

      setAirportIds({ from: originAirportIds, to: destinationAirportIds });

      if (request?.cargo_details === 'Gross Weight') {
        const cargoWithNullWeights = request.cargo.map((cargoItem) => ({ ...cargoItem, weight: null }));
        setRequest({ ...request, cargo: cargoWithNullWeights });
      }
    }
  }, [request, setRequest]);

  useEffect(() => {
    if (default_contacts?.length) {
      const contacts = default_contacts.map((contact) => ({
        contact_id: String(contact.contact_id),
        updates_for: contact.updates_for,
      }));
      methods.setValue(
        'default_contacts',
        contacts.map((contact) => ({ contact_id: contact.contact_id, updates_for: contact.updates_for })),
      );
    }
  }, [default_contacts, methods]);

  const calculations: Calculations = useMemo(() => {
    const vw = request?.volumetric_weight;
    const gw = request?.gross_weight;

    const cw = Math.max(vw, gw);

    return { cw, vw, gw };
  }, [request?.volumetric_weight, request?.gross_weight]);

  const handleModalSubmit = (values: RequestFormValues) => {
    const formattedRequestForm = formatRequestForm(values);

    setRequest(formattedRequestForm);
    onClose();
  };

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        const formattedRequest = formatRequestForm(request, { isFinalFormat: true });
        // Clamp the freight service to what this account type is allowed to select (per-tenant config).
        const allowedFreightServices = getFreightServices(targetAccount?.type);
        const freightService = allowedFreightServices.includes(formattedRequest?.freight_service)
          ? formattedRequest?.freight_service
          : allowedFreightServices[0];
        // Air-freight-only shipments have no inland consolidation / freight add-ons.
        const isAfrOnly = freightService === FreightService.AFR;
        const data = {
          ...formattedRequest,
          ...values,
          freight_service: freightService,
          is_consolidated: isAfrOnly ? false : formattedRequest.is_consolidated,
          extra_freight_services: isAfrOnly ? [] : formattedRequest?.extra_freight_services,
        };

        const cleaned = omit(data, [
          'total_pieces',
          'service_type',
          'flight',
          'is_terms_accepted',
          'battery_type_variant',
          'dgdTab',
          'chargeable_weight',
          'volumetric_weight',
        ]);
        const nextCleaned = {
          ...cleaned,
          cargo: cleaned.cargo.map((cargo) => ({
            ...omit(cargo, 'packing'),
            handling: [cargo?.handling, ...(cargo?.packing || [])],
          })),
        };

        const nextRequest = await createRequest(nextCleaned);

        if (nextRequest?.id) {
          setRequest(defaultRequestFormValues);
          navigate(`/requests/${nextRequest.id}`, {
            state: {
              from: '/request-quote',
              request: nextRequest,
            },
          });
        }
      } catch (error) {
        toast({
          title: 'Something went wrong',
          description: error?.message,
          status: 'error',
        });
      }
    },
    [request, targetAccount?.type, createRequest, setRequest, navigate, toast],
  );

  const renderProvider = (provider) => {
    return (
      <WrapItem key={provider?.id}>
        <Tag color="offBlack" fontWeight="semibold" variant="outline" rounded="full" size="md">
          {provider?.name}
        </Tag>
      </WrapItem>
    );
  };

  const renderCheckedTag = (value: string) => {
    return (
      <WrapItem key={value}>
        <Tag size="md">{value}</Tag>
      </WrapItem>
    );
  };

  const dimensionUnit = request?.original_dimension_unit;
  const weightUnit = request?.original_weight_unit;
  const isInLbs = weightUnit === WeightUnit.lb;

  const renderCargoItem = (item: CargoItem) => {
    const length = item?.length;
    const width = item?.width;
    const height = item?.height;

    return (
      <>
        <GridItem>
          <Text textStyle="bodySmallSemibold">{item?.quantity} pcs</Text>
        </GridItem>
        <GridItem>
          <Text textStyle="bodySmallSemibold">
            {prettyNumber(length)}x{prettyNumber(width)}x{prettyNumber(height)} {getPrettyDimensionUnit(dimensionUnit)}
          </Text>
        </GridItem>
        <GridItem>
          <Show if={isInLbs}>
            <Tooltip
              label={`This weight has been converted from ${prettyNumber(item?.weight)} LBs to ${prettyNumber(lbToKg(item?.weight))} KGs`}
            >
              <Text textStyle="bodySmallSemibold">{prettyNumber(lbToKg(item?.weight))} kg</Text>
            </Tooltip>
          </Show>
          <Show if={!isInLbs}>
            <Text textStyle="bodySmallSemibold">{prettyNumber(item?.weight)} kg</Text>
          </Show>
        </GridItem>
        <GridItem>
          <Text textStyle="bodySmallSemibold">{getHandlingAndPacking(item)}</Text>
        </GridItem>
      </>
    );
  };

  const cargoType = getPrettyCargoType(request?.cargo_type);
  const zipOrigin = useRequestPickupOrigin({
    freight_services: request?.freight_service === FreightService.EXW ? [FreightService.EXW] : [],
    cargo_origin: request?.cargo_origin,
  });

  return (
    <Page title="Request Quote" flex="1">
      <Container w="full" maxW={CONTAINER_2XL} p="0">
        <Stack px={{ base: '2.5', sm: '4', md: '5', lg: '6' }} py={{ base: '2.5', lg: '10' }} spacing="6">
          <Stack spacing="0" w="full">
            <Text textStyle="label">ROUTE</Text>
            <Heading variant="h3">
              {zipOrigin ?? join(request?.origin_airports)} → {join(request?.destination_airports)}
            </Heading>
          </Stack>
          <Stack direction={{ base: 'column', lg: 'row' }} w="full" spacing="6">
            <Stack w="full" spacing="8">
              <Hint name="requestQuote-requestSummary">
                <Card h="inherit" px="4" py="3">
                  <CardBody p="0">
                    <Stack spacing="8">
                      <HStack justify="space-between">
                        <Heading size="md">Cargo Info</Heading>
                        <Button onClick={onOpen} leftIcon={<HiPencil />}>
                          Edit
                        </Button>
                      </HStack>
                      <SimpleGrid columns={2} gap={4}>
                        <Stack direction="column">
                          <LabeledValue label="Cargo Type" value={cargoType ?? '-'} />
                          <LabeledValue
                            label="Commodity"
                            value={getPrettyCommodity(request?.commodity)}
                            defaultValue="-"
                          />
                          <LabeledValue label="Cargo Details" value={request?.cargo_details ?? '-'} />
                          <LabeledValue label="Expected Drop-Off" value={prettyDate(request?.dropoff_by)} />
                          <LabeledValue label="Known Shipper" value={request?.is_known_shipper ? 'Yes' : 'No'} />
                        </Stack>
                        <Stack direction="column">
                          <LabeledValue label="Gross Weight" value={prettyNumber(calculations?.gw)} />
                          <LabeledValue label="Volumetric Weight" value={prettyNumber(calculations?.vw)} />
                          <LabeledValue label="Chargeable Weight" value={prettyNumber(calculations?.cw)} />
                          <LabeledValue label="Units">
                            <HStack w="fit-content">
                              {renderCheckedTag(request?.original_weight_unit)}
                              {renderCheckedTag(request?.original_dimension_unit)}
                            </HStack>
                          </LabeledValue>
                        </Stack>
                      </SimpleGrid>
                      <LabeledValue label="Cargo" overflowX="auto">
                        <Stack>
                          <Grid gridTemplateColumns="repeat(4, auto)" columnGap={3} rowGap={2}>
                            {request?.cargo?.slice(0, showAllCargo ? request?.cargo?.length : 3)?.map(renderCargoItem)}
                          </Grid>
                          <Show if={request?.cargo?.length > 3}>
                            <Button variant="outline" onClick={toggleShowAllCargo} alignSelf="flex-start">
                              {showAllCargo ? 'View less' : 'View more'}
                            </Button>
                          </Show>
                        </Stack>
                      </LabeledValue>
                    </Stack>
                  </CardBody>
                </Card>
              </Hint>

              <Card h="inherit" px="4" py="3">
                <CardBody p="0">
                  <Stack spacing="8">
                    <Heading size="md">Service Request</Heading>
                    <Wrap>
                      {Object.values(AircraftType)?.map((value) => renderCheckedTag(getPrettyAircraftType(value)))}
                    </Wrap>
                    <Wrap>{uniqBy(providers, 'name')?.map(renderProvider)}</Wrap>
                  </Stack>
                </CardBody>
              </Card>
            </Stack>
            <SimpleForm
              onSubmit={handleSubmit}
              defaultValues={{ ...request, default_contacts }}
              {...(methods as UseFormReturn<any, any>)}
            >
              <Stack w="full" spacing="6">
                <Card h="initial" px="4" py="3">
                  <CardBody p="0">
                    <Stack spacing={{ base: '6', lg: '10' }} h="full">
                      <Stack spacing="6">
                        <Stack spacing="0">
                          <Heading variant="h4">Additional Information (optional)</Heading>
                          <Text textStyle="captionRegular">
                            Please let us know any other relevant information such as cargo description, airline
                            preferences, gateway considerations, etc
                          </Text>
                        </Stack>

                        <SimpleTextarea
                          name="additional_information"
                          variant="outline"
                          placeholder="Tell us more about your quote..."
                          rows={8}
                        />
                      </Stack>
                      <Stack spacing="6">
                        <Stack spacing="0">
                          <Heading variant="h4">Your Reference Number</Heading>
                          <Text textStyle="captionRegular">
                            Please enter the tracking number from your TMS to have it included in all communications.
                          </Text>
                        </Stack>
                        <SimpleInput
                          name="customer_ref"
                          variant="outline"
                          placeholder="Please enter the tracking number from your TMS"
                        />
                      </Stack>
                      <Stack spacing="6" w="full">
                        <Stack spacing="0">
                          <Heading variant="h4">Spot Quotes</Heading>
                          <Text textStyle="captionRegular">
                            Do you have the freight or know what is needed to win it?
                          </Text>
                        </Stack>
                        <Stack
                          direction={{ base: 'column', lg: 'row' }}
                          align={{ base: 'auto', lg: 'center' }}
                          w="full"
                          spacing="6"
                        >
                          <SimpleSwitch
                            name="freight_status"
                            label="Freight"
                            options={freightStatusOptions}
                            w="full"
                            variant="outline"
                          />
                          <SimpleInput
                            name="target_rate"
                            label="Target Rate"
                            placeholder="Target Rate"
                            autoComplete="off"
                            w="full"
                            type="number"
                            variant="outline"
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack flex="1" direction="column" justify="flex-end" spacing="4" pt="5">
                      <SimpleCheckbox
                        variant="unstyled"
                        colorScheme="primary"
                        name="is_terms_accepted"
                        size="lg"
                        label="By checking, I accept the Terms of Service and Privacy Policy"
                      />

                      <Button type="submit" colorScheme="primary" size="lg" borderRadius="md" isLoading={isLoading}>
                        Request Quotes
                      </Button>
                    </Stack>
                  </CardBody>
                </Card>
                <Card h="auto" px="4" py="3">
                  <CardBody p="0">
                    <DefaultContactsCard notificationCategory={NotificationCategory.Quote} />
                  </CardBody>
                </Card>
              </Stack>
            </SimpleForm>
          </Stack>
        </Stack>
      </Container>

      <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="outside" size={{ base: 'full', lg: '5xl' }}>
        <ModalOverlay />
        <ModalContent p={{ base: '4', lg: '8' }}>
          <ModalHeader px="0">
            <Heading>Choose the best priced flights</Heading>
            <ModalCloseButton />
          </ModalHeader>
          <SimpleRequestForm submitLabel="Update" isUpdate onSubmit={handleModalSubmit} />
        </ModalContent>
      </Modal>
    </Page>
  );
};
